/**
 * As consultas da trilha de acesso (`NR-114`).
 *
 * O período é OBRIGATÓRIO na API (`desde` inclusivo, `ate` exclusivo, até 90
 * dias): toda consulta poda partição (`ADR-0007`). A tela trabalha com DIA
 * civil de São Paulo — "de 01/10 a 07/10" — e aqui vira instante: `desde` às
 * 00:00 do primeiro dia, `ate` às 00:00 do dia SEGUINTE ao último.
 */

import { queryOptions } from '@tanstack/react-query';
import type { AccessLogUser, AccessRecordPageResponse, ParametrosDaTrilhaDeAcesso } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { mapearPaginaDaTrilha, mapearUsuarioDaTrilha } from './mapeadores';
import type { AcaoDeAcesso, PaginaDaTrilha, RecursoAcessado, UsuarioDaTrilha } from './tipos';

/** Cabe no teto de `GetAccessRecordsQuery.MaxPageSize` (100). */
export const TAMANHO_DE_PAGINA = 50;

/** Espelha `GetAccessRecordsQuery.MaxPeriodDays`. */
export const PERIODO_MAXIMO_EM_DIAS = 90;

/** Quantos dias a tela abre por padrão. */
export const PERIODO_PADRAO_EM_DIAS = 7;

/**
 * Offset de São Paulo. Fixo porque o Brasil não tem horário de verão desde
 * 2019 — e o dia civil da tela é o de quem opera, não o do servidor.
 */
const OFFSET_DE_SAO_PAULO = '-03:00';

const DIA_ISO = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** O que a tela escolhe. Dias em `AAAA-MM-DD`, os dois inclusivos. */
export interface FiltrosDaTrilha {
  readonly pagina: number;
  readonly desde: string;
  readonly ate: string;
  readonly usuarioId: string | null;
  readonly acao: AcaoDeAcesso | null;
  readonly recurso: RecursoAcessado | null;
  readonly casoId: string | null;
  readonly ticketId: string | null;
}

export const chavesDaTrilha = {
  raiz: ['trilha-de-acesso'] as const,
  lista: (parametros: ParametrosDaTrilhaDeAcesso) => ['trilha-de-acesso', 'lista', parametros] as const,
  usuarios: ['trilha-de-acesso', 'usuarios'] as const,
};

export function ehDiaValido(dia: string | null): dia is string {
  return dia !== null && DIA_ISO.test(dia) && !Number.isNaN(Date.parse(`${dia}T00:00:00Z`));
}

export function ehUuid(valor: string | null): valor is string {
  return valor !== null && UUID.test(valor.trim());
}

/** Soma dias a um dia civil `AAAA-MM-DD`. Aritmética em UTC: dia civil não tem fuso. */
export function somarDias(dia: string, dias: number): string {
  const instante = new Date(`${dia}T00:00:00Z`);
  instante.setUTCDate(instante.getUTCDate() + dias);
  return instante.toISOString().slice(0, 10);
}

/** Quantos dias o período cobre, contando os dois extremos. */
export function diasNoPeriodo(desde: string, ate: string): number {
  return Math.round((Date.parse(`${ate}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000) + 1;
}

/** Hoje, como dia civil de São Paulo. */
export function hojeEmSaoPaulo(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(agora);
}

export function montarParametros(filtros: FiltrosDaTrilha): ParametrosDaTrilhaDeAcesso {
  return {
    desde: `${filtros.desde}T00:00:00${OFFSET_DE_SAO_PAULO}`,
    ate: `${somarDias(filtros.ate, 1)}T00:00:00${OFFSET_DE_SAO_PAULO}`,
    page: filtros.pagina,
    pageSize: TAMANHO_DE_PAGINA,
    ...(filtros.usuarioId !== null ? { usuarioId: filtros.usuarioId } : {}),
    ...(filtros.acao !== null ? { acao: filtros.acao } : {}),
    ...(filtros.recurso !== null ? { recurso: filtros.recurso } : {}),
    ...(filtros.casoId !== null ? { casoId: filtros.casoId } : {}),
    ...(filtros.ticketId !== null ? { ticketId: filtros.ticketId } : {}),
  };
}

function montarConsulta(parametros: ParametrosDaTrilhaDeAcesso): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) busca.set(chave, String(valor));
  return `?${busca.toString()}`;
}

/** Uma página da trilha. `pagina` é 1-based, como a API. */
export function opcoesDaTrilhaDeAcesso(filtros: FiltrosDaTrilha) {
  const parametros = montarParametros(filtros);
  return queryOptions({
    queryKey: chavesDaTrilha.lista(parametros),
    queryFn: ({ signal }): Promise<PaginaDaTrilha> =>
      requisitar<AccessRecordPageResponse>(`/auditoria/acessos${montarConsulta(parametros)}`, { sinal: signal }).then(
        mapearPaginaDaTrilha,
      ),
  });
}

/** Quem pode aparecer no filtro de usuário. Muda pouco: fica em cache por 5 min. */
export function opcoesDosUsuariosDaTrilha() {
  return queryOptions({
    queryKey: chavesDaTrilha.usuarios,
    queryFn: ({ signal }): Promise<UsuarioDaTrilha[]> =>
      requisitar<AccessLogUser[]>('/auditoria/usuarios', { sinal: signal }).then((usuarios) =>
        usuarios.map(mapearUsuarioDaTrilha),
      ),
    staleTime: 5 * 60_000,
  });
}
